import { useCallback, useEffect, useId } from 'react';
import { toast } from 'react-toastify';
import { useShallow } from 'zustand/react/shallow';

import { useFetchWerklijnenOpId } from '@/components/search/hooks/useFetchWerklijnenOpId';
import useClientState from '@/state/useClientState';
import { WerklijnInline } from '@/types/werklijnenInline';
import { retry } from '@/utils/retry';

import { werklijnComparator } from '../utils/werklijnen';

/**
 * Samen met `useVerwerkWerklijnEvent()` de belangrijkste hook binnen deze file. Hiermee kan een component zich
 * subscriben op een bepaalde lijst van werklijnen.
 *
 * @return een object met de properties `werklijnen` wat de weer te geven werklijnen zijn,
 * en een `vervangWerklijnen` functie waarmee de lijst kan worden aangepast, bijv. na het uitvoeren
 * van een zoekquery.
 *
 * De werklijnen in de `werklijnen` property worden reactive geupdate als 1 of meerdere werklijnen
 * in de lijst zijn bijgewerkt. Werklijnen worden ook automatisch verwijderd uit de lijst als een verwijderd-event
 * is binnengekomen.
 *
 *
 * Voorbeeld-gebruik:
 *
 * ```ts
 * function MyComponent(props: MyPropType) {
 *  const { werklijnen, vervangWerklijnen } = useWerklijnenSubscription();
 *
 *  // toon werklijnen aan de gebruiker en update m.b.v. vervangWerklijnen():
 *  (...)
 * }
 * ```
 */
const useWerklijnenSubscription = () => {
  const subId = useId();
  const { fetchEnkeleWerklijnOpId } = useFetchWerklijnenOpId();
  const vervangWerklijnen = useClientState((state) => state.vervangWerklijnen);
  const voegMeerdereWerklijnenToe = useClientState((state) => state.voegMeerdereWerklijnenToe);
  const plaatsWerklijnBovenaan = useClientState((state) => state.plaatsWerklijnBovenaan);
  const setSortering = useClientState((state) => state.setSortering);
  const voegWerklijnToe = useClientState((state) => state.voegWerklijnToe);
  const ruimSubscriptionOp = useClientState((state) => state.ruimSubscriptionOp);
  const verwijderMeerdereWerklijnen = useClientState((state) => state.verwijderMeerdereWerklijnen);

  const vervangWerklijnenCallback = useCallback(
    (werklijnen: WerklijnInline[]) => vervangWerklijnen(subId, werklijnen),
    [subId, vervangWerklijnen],
  );

  const werklijnenView = useClientState(
    useShallow((state) => {
      if (state.werklijnenSubs[subId]) {
        return state.werklijnenSubs[subId]
          .map((e) => state.werklijnen[e])
          .sort((a, b) => werklijnComparator(a, b, state.werklijnSortering));
      }
      return null;
    }),
  );

  const sorteerWerklijnenCallback = useCallback(
    (sortering: 'eenheid' | 'uitloop' | 'treinnummer' | 'inzetbaarheid') => {
      setSortering(sortering);
    },
    [setSortering],
  );

  const plaatsWerklijnBovenaanCallback = useCallback(
    (werklijn: WerklijnInline) => plaatsWerklijnBovenaan(werklijn),
    [plaatsWerklijnBovenaan, subId],
  );

  const voegwerklijnToeCallback = useCallback(
    async (werklijnId: string) => {
      try {
        const werklijn = await retry(fetchEnkeleWerklijnOpId, 25, werklijnId);
        voegWerklijnToe(subId, werklijn);
      } catch (error) {
        console.log(error);
        toast.error('Toevoegen van nieuwe werklijn is niet gelukt');
      }
    },
    [fetchEnkeleWerklijnOpId, voegWerklijnToe, subId],
  );

  const voegMeerdereWerklijnenToeCallback = useCallback(
    (werklijnen: WerklijnInline[], toppen: boolean, suppressToast?: boolean) => {
      voegMeerdereWerklijnenToe(subId, werklijnen, toppen, suppressToast);
    },
    [subId, voegMeerdereWerklijnenToe],
  );

  const verwijderWerklijnenCallback = useCallback(
    async (werklijnId: string[]) => {
      verwijderMeerdereWerklijnen(werklijnId);
    },
    [verwijderMeerdereWerklijnen],
  );

  // bij unmount netjes de werklijnen uit de view opruimen:
  useEffect(() => {
    return () => ruimSubscriptionOp(subId);
    // We kunnen hier de dependency-array leeglaten omdat `subId` en `ruimSubscriptionOp` gegarandeerd stable zijn:
  }, []);

  return {
    werklijnen: werklijnenView,
    sorteerWerklijnen: sorteerWerklijnenCallback,
    vervangWerklijnen: vervangWerklijnenCallback,
    voegMeerdereWerklijnenToe: voegMeerdereWerklijnenToeCallback,
    voegWerklijnToe: voegwerklijnToeCallback,
    plaatsWerklijnBovenaan: plaatsWerklijnBovenaanCallback,
    verwijderMeerdereWerklijnen: verwijderWerklijnenCallback,
  };
};
export default useWerklijnenSubscription;
